/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { AuthService } from '@alfa-client/app-shared';
import {
  ApiError,
  HttpErrorHandler,
  isForbidden,
  isServerError,
  isUnauthorized,
  serverErrorAction,
} from '@common/tech-shared';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { DialogService } from '../dialog/dialog.service';
import { Messages } from '../messages';
import { SnackBarService } from '../snackbar/snackbar.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly dialogService = inject(DialogService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly errorHandler = inject(HttpErrorHandler);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const defaultHandling: boolean = this.errorHandler.shouldDoDefaultHandling();
    return next.handle(request).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        defaultHandling ? this.handleError(errorResponse) : throwError(() => errorResponse),
      ),
      finalize(() => this.errorHandler.enableDefaultHandling()),
    );
  }

  handleError(errorResponse: HttpErrorResponse): Observable<any> {
    if (isServerError(errorResponse.status)) {
      this.handleServerError(errorResponse.error);
      return EMPTY;
    }

    if (isUnauthorized(errorResponse.status)) {
      this.handleUnauthorizedError();
      return EMPTY;
    }

    if (isForbidden(errorResponse.status)) {
      this.handleForbiddenError();
    }

    return throwError(() => errorResponse);
  }

  private handleServerError(apiError: ApiError): void {
    this.store.dispatch(serverErrorAction({ apiError }));
    this.dialogService.openApiErrorInfo(apiError);
  }

  handleUnauthorizedError(): void {
    this.authService.logout();
  }

  handleForbiddenError(): void {
    if (!this.snackbarService.isVisible()) {
      this.snackbarService.showError(Messages.HTTP_STATUS_FORBIDDEN);
    }
  }
}
